import { RepeatingFieldGroup } from '@features/service-form/repeating-field-group';
import { ConditionalFieldManager } from '@features/service-form/conditional-field-manager';
import { FormAutoSave } from '@features/service-form/form-auto-save';
import { FormClipboardExporter } from '@features/service-form/form-clipboard-exporter';
import { serviceFormSubmitter } from '@features/service-form/service-form-submitter';
import type { ServiceFormPageConfig } from '@features/service-form/service-form-types';

export class ServiceFormPage {
  private form: HTMLFormElement;
  private autoSave: FormAutoSave;
  private groups: RepeatingFieldGroup[] = [];
  private conditional: ConditionalFieldManager | null = null;
  private clipboardExporter: FormClipboardExporter | null = null;
  private config: ServiceFormPageConfig;

  constructor(config: ServiceFormPageConfig) {
    this.config = config;

    const formEl =
      typeof config.formEl === 'string' ? document.getElementById(config.formEl) : config.formEl;

    if (!(formEl instanceof HTMLFormElement)) {
      throw new Error(`ServiceFormPage: form element not found (${String(config.formEl)})`);
    }
    this.form = formEl;

    this.autoSave = new FormAutoSave({
      formType: config.formType,
      serverPrefill: config.serverPrefill,
      excludeFromPrefill: config.excludeFromPrefill,
    });

    for (const groupConfig of config.repeatingGroups ?? []) {
      const group = new RepeatingFieldGroup({
        ...groupConfig,
        onChange: () => this.autoSave.scheduleSave(),
      });
      this.autoSave.registerGroup(groupConfig.containerId, group);
      this.groups.push(group);
    }

    this.autoSave.attach(this.form);

    if (config.conditionals?.length) {
      this.conditional = new ConditionalFieldManager(this.form, config.conditionals);
    }

    if (config.clipboardExport) {
      this.clipboardExporter = new FormClipboardExporter(this.form, config.clipboardExport);
      this.clipboardExporter.attachButton();
    }

    this.bindSubmit();

    void this.autoSave.restore().then(() => {
      this.conditional?.evaluate();
    });
  }

  private bindSubmit(): void {
    this.form.addEventListener('submit', e => {
      e.preventDefault();
      const submitBtn = this.form.querySelector<HTMLButtonElement>('button[type="submit"]') ?? null;

      void serviceFormSubmitter.submit({
        formType: this.config.formType,
        getData: () => new FormData(this.form),
        preSubmit: this.config.preSubmit,
        transform: this.config.transform,
        onClearCache: () => this.autoSave.clear(),
        messages: this.config.messages,
        submitButtonEl: submitBtn,
        failureAction: this.clipboardExporter
          ? {
              label: '复制填写信息',
              action: () => {
                void this.clipboardExporter?.copy();
              },
            }
          : undefined,
      });
    });
  }

  destroy(): void {
    this.groups.forEach(g => g.destroy());
    this.conditional?.destroy();
  }
}
